/*
 * Sends frontend errors to the backend (POST /api/client-errors).
 * - Never throws: always resolves to { reported: boolean, reportId?: string }.
 * - De-duplicates identical errors (same source + message within 10 s) by reusing the first result.
 * - Caps the number of reports per page load.
 * - Ignores aborts and benign browser noise (ResizeObserver, browser extensions).
 */

const ENDPOINT_PATH = "/api/client-errors";
const REQUEST_TIMEOUT_MS = 5000;
const TOKEN_TIMEOUT_MS = 2000;
const DEDUPE_WINDOW_MS = 10000;
const MAX_REPORTS_PER_PAGE = 10;
const MAX_FIELD_LENGTH = 8000;
const MAX_BODY_BYTES = 32000;

const EXTENSION_URL = /^(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//;
const BENIGN_MESSAGES = [/ResizeObserver loop/i, /^Script error\.?$/i];

let tokenGetter = null;
let reportCount = 0;
const recent = new Map(); // key -> { at, promise }

/* Register a function returning the Clerk session token (or null). Pass null to clear. */
export function setErrorReporterTokenGetter(getter) {
  tokenGetter = typeof getter === "function" ? getter : null;
}

/* Test helper: clears de-duplication state and the per-page counter */
export function resetErrorReporter() {
  reportCount = 0;
  recent.clear();
  tokenGetter = null;
}

export function getErrorMessage(error) {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;
  if (typeof error.message === "string" && error.message) return error.message;
  if (typeof error.statusText === "string" && error.statusText) return error.statusText;
  try {
    return String(error);
  } catch {
    return "Unknown error";
  }
}

/* True for errors that should be neither reported nor shown to the user */
export function isIgnorableError(error, { filename } = {}) {
  if (!error && !filename) return false;
  if (error?.name === "AbortError") return true;

  const message = getErrorMessage(error);
  if (BENIGN_MESSAGES.some((re) => re.test(message))) return true;

  if (filename && EXTENSION_URL.test(filename)) return true;

  // Errors whose stack only references browser-extension scripts
  const stack = typeof error?.stack === "string" ? error.stack : "";
  const urls = stack.match(/[a-z][a-z0-9+.-]*:\/\/[^\s)]+/gi) || [];
  if (urls.length > 0 && urls.every((url) => EXTENSION_URL.test(url))) return true;

  return false;
}

function truncate(value, max = MAX_FIELD_LENGTH) {
  if (value === undefined || value === null) return undefined;
  const str = String(value);
  return str.length > max ? `${str.slice(0, max - 15)}… [truncated]` : str;
}

function byteLength(str) {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(str).length;
  return str.length * 3; // conservative upper bound
}

function safeExtra(extra) {
  if (!extra || typeof extra !== "object") return undefined;
  try {
    const json = JSON.stringify(extra);
    if (json.length <= 2000) return JSON.parse(json);
    return { truncated: truncate(json, 2000) };
  } catch {
    return undefined;
  }
}

export function buildReportBody(error, { source, componentStack, requestId, status, extra } = {}) {
  const httpStatus = status ?? error?.status;
  const body = {
    source,
    message: truncate(getErrorMessage(error)),
    name: truncate(error?.name || (typeof error === "string" ? "Error" : undefined)),
    stack: truncate(error?.stack),
    componentStack: truncate(componentStack),
    url: truncate(typeof window !== "undefined" ? window.location.href : undefined),
    userAgent: truncate(typeof navigator !== "undefined" ? navigator.userAgent : undefined),
    requestId: truncate(requestId ?? error?.requestId, 200),
    status: Number.isFinite(httpStatus) ? httpStatus : undefined,
    extra: safeExtra(extra),
  };

  // Keep the whole body under the backend limit: shrink the biggest fields first
  const steps = [
    () => delete body.extra,
    () => (body.componentStack = truncate(body.componentStack, 2000)),
    () => (body.stack = truncate(body.stack, 2000)),
    () => delete body.componentStack,
    () => delete body.stack,
    () => (body.message = truncate(body.message, 2000)),
  ];
  for (const step of steps) {
    if (byteLength(JSON.stringify(body)) <= MAX_BODY_BYTES) break;
    step();
  }
  return body;
}

async function getTokenSafely() {
  if (!tokenGetter) return null;
  try {
    return await Promise.race([
      Promise.resolve(tokenGetter()),
      new Promise((resolve) => setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS)),
    ]);
  } catch {
    return null;
  }
}

async function send(body) {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
  try {
    const token = await getTokenSafely();
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}${ENDPOINT_PATH}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller?.signal,
    });
    if (!response?.ok) return { reported: false };
    const data = await response.json().catch(() => ({}));
    return { reported: true, reportId: typeof data?.reportId === "string" ? data.reportId : undefined };
  } catch {
    return { reported: false };
  } finally {
    clearTimeout(timer);
  }
}

/*
 * reportError(error, { source, componentStack, requestId, status, extra })
 * source: "boundary" | "router" | "window" | "unhandledrejection" | "api"
 */
export function reportError(error, meta = {}) {
  try {
    if (isIgnorableError(error)) return Promise.resolve({ reported: false, ignored: true });

    const key = `${meta.source || "unknown"}|${getErrorMessage(error)}`;
    const now = Date.now();
    for (const [k, entry] of recent) {
      if (now - entry.at > DEDUPE_WINDOW_MS) recent.delete(k);
    }
    const previous = recent.get(key);
    if (previous) return previous.promise;

    if (reportCount >= MAX_REPORTS_PER_PAGE) return Promise.resolve({ reported: false });
    reportCount += 1;

    const promise = send(buildReportBody(error, meta)).catch(() => ({ reported: false }));
    recent.set(key, { at: now, promise });
    return promise;
  } catch {
    return Promise.resolve({ reported: false });
  }
}
